import { useState } from 'react';
import { HopperLogo } from '../ui/HopperLogo';
import { HopperIcon } from '../ui/icons/HopperIcon';
import './onboarding.css';

export const ONBOARDING_COMPLETED_KEY = 'hopper.onboarding.v1.completed';

type PermissionState = 'idle' | 'requesting' | 'allowed' | 'denied' | 'unavailable';

type OnboardingScreenProps = {
  onComplete?: () => void;
};

function permissionButtonClass(state: PermissionState): string {
  return state === 'allowed' ? 'onboarding-permission is-allowed' : 'onboarding-permission';
}

export function OnboardingScreen({ onComplete }: OnboardingScreenProps) {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [cameraState, setCameraState] = useState<PermissionState>('idle');
  const [locationState, setLocationState] = useState<PermissionState>('idle');

  const requestCamera = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setCameraState('unavailable');
      return;
    }

    setCameraState('requesting');

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      stream.getTracks().forEach((track) => track.stop());
      setCameraState('allowed');
    } catch (error) {
      const name = error instanceof DOMException ? error.name : '';
      setCameraState(name === 'NotAllowedError' || name === 'SecurityError' ? 'denied' : 'unavailable');
    }
  };

  const requestLocation = () => {
    if (!navigator.geolocation) {
      setLocationState('unavailable');
      return;
    }

    setLocationState('requesting');
    navigator.geolocation.getCurrentPosition(
      () => setLocationState('allowed'),
      (error) => setLocationState(error.code === error.PERMISSION_DENIED ? 'denied' : 'unavailable'),
      { enableHighAccuracy: false, timeout: 12000, maximumAge: 0 },
    );
  };

  const finish = () => {
    try {
      localStorage.setItem(ONBOARDING_COMPLETED_KEY, '1');
    } catch {
      // Storage can be unavailable in hardened browser modes; onboarding still must not block the app.
    }

    if (onComplete) {
      onComplete();
      return;
    }

    window.location.assign('/pulse');
  };

  return (
    <main className="onboarding-page" aria-label="Знакомство с Хоппер">
      <section className="onboarding-card">
        <div className="onboarding-brand"><HopperLogo /></div>
        <div className="onboarding-progress" aria-label={`Шаг ${step} из 3`}>
          {[1, 2, 3].map((value) => <span key={value} className={value <= step ? 'is-active' : ''} />)}
        </div>

        {step === 1 && (
          <div className="onboarding-step">
            <div className="onboarding-hero-icon"><HopperIcon name="sparkle" size={32} /></div>
            <p className="onboarding-kicker">Начнём с главного</p>
            <h1>Добро пожаловать в Хоппер</h1>
            <p className="onboarding-lead">Сканируйте чеки, получайте XP и влияйте на рейтинг любимых заведений</p>
            <button className="onboarding-primary" type="button" onClick={() => setStep(2)}>
              Далее <HopperIcon name="arrow-right" size={18} />
            </button>
          </div>
        )}

        {step === 2 && (
          <div className="onboarding-step">
            <p className="onboarding-kicker">Три простых действия</p>
            <h1>Как это работает</h1>
            <div className="onboarding-how-list">
              <div><span><HopperIcon name="scan" size={22} /></span><strong>Сканируйте чек после визита</strong></div>
              <div><span><HopperIcon name="xp" size={22} /></span><strong>Получайте XP за подтверждённые визиты</strong></div>
              <div><span><HopperIcon name="trophy" size={22} /></span><strong>Влияйте на рейтинг заведений</strong></div>
            </div>
            <button className="onboarding-primary" type="button" onClick={() => setStep(3)}>
              Далее <HopperIcon name="arrow-right" size={18} />
            </button>
          </div>
        )}

        {step === 3 && (
          <div className="onboarding-step">
            <div className="onboarding-hero-icon"><HopperIcon name="location" size={30} /></div>
            <p className="onboarding-kicker">Можно пропустить</p>
            <h1>Разрешения</h1>
            <p className="onboarding-lead">Камера нужна только для сканирования чеков, а геолокация — для функции «Рядом». Читать Хоппер и выбрать город вручную можно без этих разрешений.</p>

            <div className="onboarding-permissions">
              <button
                className={permissionButtonClass(cameraState)}
                type="button"
                disabled={cameraState === 'requesting'}
                onClick={() => void requestCamera()}
              >
                <span><HopperIcon name="camera" size={20} />Разрешить камеру</span>
                {cameraState === 'allowed' && <HopperIcon name="check" size={20} />}
              </button>
              {cameraState === 'denied' && <p className="onboarding-permission-note is-warning">Камера не разрешена. Вы можете разрешить её позже в настройках браузера</p>}
              {cameraState === 'unavailable' && <p className="onboarding-permission-note is-warning">Камера недоступна на этом устройстве</p>}

              <button
                className={permissionButtonClass(locationState)}
                type="button"
                disabled={locationState === 'requesting'}
                onClick={requestLocation}
              >
                <span><HopperIcon name="location" size={20} />Разрешить геолокацию</span>
                {locationState === 'allowed' && <HopperIcon name="check" size={20} />}
              </button>
              {locationState === 'denied' && <p className="onboarding-permission-note is-warning">Геолокация не разрешена. Карта будет работать, но без фильтра «Рядом»</p>}
              {locationState === 'unavailable' && <p className="onboarding-permission-note is-warning">Геолокация недоступна на этом устройстве</p>}
            </div>

            <button className="onboarding-primary" type="button" onClick={finish}>Начать использовать Хоппер</button>
            <p className="onboarding-footnote">Оба разрешения необязательны. Их можно выдать или изменить позже в настройках браузера.</p>
          </div>
        )}
      </section>
    </main>
  );
}
